import { useTranslation } from 'react-i18next'
import { Button } from './Button'

const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    aria-hidden="true"
    style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
)

interface DetailsToggleButtonProps {
  expanded: boolean
  onClick: () => void
  /** Per-row aria-label (e.g. `Expand ${email}`) so multiple rows on the same page stay
   *  individually addressable to assistive tech and tests. Falls back to the plain "Details"
   *  label when the row is already unambiguous (e.g. a standalone expanded-row action strip). */
  ariaLabel?: string
  /** "inline" — compact icon+text link for a desktop action strip or an inline mobile row
   *  (default). "block" — full-width secondary button for its own row in a mobile card. */
  variant?: 'inline' | 'block'
  /** Tighter padding for dense action strips (e.g. next to other small icon buttons). */
  small?: boolean
  className?: string
}

/**
 * Shared "Details" expand/collapse toggle for the event management participant tables
 * (Registered, Rejected, Accepted, Attended). Centralizes icon + label so every table shows the
 * exact same affordance — previously some rows showed only the chevron icon (no visible label)
 * while others showed icon + "Details" text.
 */
export function DetailsToggleButton({
  expanded,
  onClick,
  ariaLabel,
  variant = 'inline',
  small = false,
  className = '',
}: DetailsToggleButtonProps) {
  const { t } = useTranslation()
  const label = t('reg_expand')

  if (variant === 'block') {
    return (
      <Button
        type="button"
        variant="secondary"
        size="sm"
        fullWidth
        leftIcon={<ChevronIcon open={expanded} />}
        onClick={onClick}
        aria-label={ariaLabel}
        aria-expanded={expanded}
        className={className}
      >
        {label}
      </Button>
    )
  }

  return (
    <Button
      type="button"
      variant="unstyled"
      onClick={onClick}
      aria-label={ariaLabel ?? label}
      aria-expanded={expanded}
      className={[
        'text-pax-gray hover:text-pax-black flex shrink-0 items-center gap-1 rounded transition-colors',
        small ? 'h-6 px-1.5 text-xs' : 'px-2 py-1 text-xs',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <ChevronIcon open={expanded} />
      {label}
    </Button>
  )
}
